import { inject, Injectable } from '@angular/core';
import { FormError } from '@shared/interfaces/form-error';
import { Notifier } from './notifier.service';
import { TranslateService } from '@devinforius/i-essentials';
import { DataError } from '@shared/models/data-error';

@Injectable({
  providedIn: 'root',
})
export class ErrorHandler {
  private readonly notifier = inject(Notifier);
  private readonly translater = inject(TranslateService);

  public handle(errors: FormError[] | DataError): void {
    if (!errors) return;

    if (Array.isArray(errors)) {
      this.handleFormErrors(errors);
    } else {
      this.handleDataError(errors);
    }
  }

  private handleFormErrors(errors: FormError[]): void {
    for (const err of errors) {
      switch (err.code) {
        case 'invalid_type':
          this.showError(
            this.translater.instant('ERRORS.FORM_ERRORS.INVALID_TYPE.TITLE', {
              property: err.property,
            }),
            this.translater.instant(
              err.message === 'Obligatoire'
                ? 'ERRORS.FORM_ERRORS.INVALID_TYPE.DESCRIPTION.MANDATORY'
                : err.message,
            ),
            8000,
          );
          break;

        case 'invalid_enum_value':
          this.showError(
            this.translater.instant(
              'ERRORS.FORM_ERRORS.INVALID_ENUM_VALUE.TITLE',
              {
                property: err.property,
              },
            ),
            err.message,
            8000,
          );
          break;

        case 'too_small':
          this.showError(
            this.translater.instant('ERRORS.FORM_ERRORS.TOO_SMALL.TITLE', {
              property: err.property,
            }),
            err.message,
            8000,
          );
          break;
      }
    }
  }

  private handleDataError(error: DataError): void {
    const message = error.message;

    const messageToShow: undefined | string = message
      ? typeof message === 'string'
        ? message
        : message.error
      : undefined;

    if (messageToShow && !error.status) {
      this.showError(
        this.translater.instant('ERRORS.DATA_ERRORS.SERVER_ERROR.TITLE', {
          ressource: error.category,
        }),
        messageToShow,
      );
      return;
    }

    switch (error.status) {
      case 404:
        this.showError(
          this.translater.instant('ERRORS.DATA_ERRORS.NOT_FOUND.TITLE', {
            ressource: error.category,
          }),
          messageToShow ||
            this.translater.instant('ERRORS.DATA_ERRORS.NOT_FOUND.DESCRIPTION'),
        );
        break;
      case 400:
        this.showError(
          this.translater.instant('ERRORS.DATA_ERRORS.BAD_REQUEST.TITLE', {
            ressource: error.category ?? 'unknown',
          }),
          messageToShow ||
            this.translater.instant(
              'ERRORS.DATA_ERRORS.BAD_REQUEST.DESCRIPTION',
            ),
        );
        break;
      case 401:
        this.showError(
          this.translater.instant('ERRORS.DATA_ERRORS.UNAUTHORIZED.TITLE', {
            ressource: error.category,
          }),
          messageToShow ||
            this.translater.instant(
              'ERRORS.DATA_ERRORS.UNAUTHORIZED.DESCRIPTION',
            ),
        );
        break;
      case 409:
        this.showError(
          this.translater.instant('ERRORS.DATA_ERRORS.CLIENT_ERROR.TITLE'),
          messageToShow ||
            this.translater.instant(
              'ERRORS.DATA_ERRORS.CLIENT_ERROR.DESCRIPTION',
              {
                serverMessage: messageToShow,
              },
            ),
        );
        break;
      case 500:
        this.showError(
          this.translater.instant('ERRORS.DATA_ERRORS.CLIENT_ERROR.TITLE'),
          this.translater.instant(
            'ERRORS.DATA_ERRORS.CLIENT_ERROR.DESCRIPTION',
            {
              serverMessage: messageToShow,
            },
          ),
        );
        break;
      default:
        this.showError(
          this.translater.instant('ERRORS.DATA_ERRORS.CLIENT_ERROR.TITLE'),
          messageToShow ||
            this.translater.instant(
              'ERRORS.DATA_ERRORS.CLIENT_ERROR.DESCRIPTION',
              {
                serverMessage: messageToShow,
              },
            ),
        );
        break;
    }
  }

  private showError(
    title: string,
    description: string,
    duration: number = 7000,
  ): void {
    this.notifier.show('error', title, description, duration);
  }
}
